import {
  formatMoney,
  type EngineAction,
  type EngineBet,
  type RoundResponse,
  type SessionInitResponse,
} from '@gp/contracts';
import { GameApi, GameApiError } from '../api';
import {
  actionSchemaOf,
  actionTypesOf,
  betSchemaOf,
  fieldsOf,
  isRequired,
  renderField,
  type Field,
} from './schema-form';

/**
 * A playable screen for a game nobody drew.
 *
 * Every game here used to need a Phaser scene before it could be played at
 * all, which made the client the slowest part of adding a game: the platform
 * would route a new engine the day it passed its gate and this build would
 * still say "no client for this game". The session already carries the
 * engine's own schema for its bets and its moves, so a plain form over that
 * schema plays any INSTANT or MULTI_STEP game the day it lands.
 *
 * It is not a replacement for a scene. A game worth shipping gets art; this
 * is what it has until then, and what an integrator uses to check a round
 * against the engine without waiting for one.
 */

export interface GenericGame {
  destroy(): void;
}

interface Options {
  api: GameApi;
  session: SessionInitResponse;
  host: HTMLElement;
  /** The shell owns the balance readout, so the round tells it what changed. */
  onBalance(balance: string): void;
}

/** Result keys the panel gives their own line to, in this order. */
const HEADLINE_KEYS = ['multiplier', 'win', 'profit', 'payout'];

/** Result keys that belong under Proof rather than in the player's face. */
const PROOF_KEYS = ['serverSeed', 'serverSeedHash', 'clientSeed', 'nonce', 'proof', 'cursors'];

export function mountGenericGame(options: Options): GenericGame {
  const { api, session, host, onBalance } = options;
  const { gameCode, schema, mode } = session.game;
  const currency = session.player.currency;
  const multiStep = mode.kind === 'MULTI_STEP';

  const betSchema = betSchemaOf(schema);
  const actionSchema = multiStep ? actionSchemaOf(schema) : null;

  // A destroyed screen must not be written to by a request that was already
  // in flight when the player went back to the lobby.
  let alive = true;
  let busy = false;
  let openRoundId: string | null = null;

  const root = document.createElement('div');
  root.className = 'gen-root';

  // ── Stake ────────────────────────────────────────────────────────────────

  const levels = session.limits.betLevels;
  let levelIndex = Math.max(0, levels.indexOf(session.limits.defaultBetLevel));

  const stakeRow = document.createElement('div');
  stakeRow.className = 'gen-stake';
  const stakeDown = button('−', 'gen-step');
  const stakeValue = document.createElement('span');
  stakeValue.className = 'gen-stake-value';
  const stakeUp = button('+', 'gen-step');
  stakeRow.append(label('Stake'), stakeDown, stakeValue, stakeUp);

  stakeDown.addEventListener('click', () => setLevel(levelIndex - 1));
  stakeUp.addEventListener('click', () => setLevel(levelIndex + 1));

  function setLevel(next: number): void {
    levelIndex = Math.min(levels.length - 1, Math.max(0, next));
    stakeValue.textContent = formatMoney(levels[levelIndex]!, currency);
    stakeDown.disabled = levelIndex === 0;
    stakeUp.disabled = levelIndex === levels.length - 1;
  }
  setLevel(levelIndex);

  // ── Bet form ─────────────────────────────────────────────────────────────

  const betPanel = panel('Your bet');
  const betFields = new Map<string, Field>();

  for (const [name, fieldSchema] of fieldsOf(betSchema)) {
    const field = renderField(name, fieldSchema, isRequired(betSchema, name));
    betFields.set(name, field);
    betPanel.body.append(field.element);
  }

  const betButton = button('Bet', 'gen-primary');
  betPanel.body.append(stakeRow, betButton);

  // ── Round in progress ────────────────────────────────────────────────────

  const roundPanel = panel('Round');
  const viewBody = document.createElement('div');
  viewBody.className = 'gen-kv';
  const actionBar = document.createElement('div');
  actionBar.className = 'gen-actions';
  const extraBody = document.createElement('div');
  extraBody.className = 'gen-extras';
  roundPanel.body.append(viewBody, extraBody, actionBar);
  roundPanel.element.hidden = true;

  // ── Result ───────────────────────────────────────────────────────────────

  const resultPanel = panel('Result');
  resultPanel.element.hidden = true;

  const failure = document.createElement('p');
  failure.className = 'gen-error';

  root.append(betPanel.element, roundPanel.element, resultPanel.element, failure);
  host.append(root);

  // ── Wiring ───────────────────────────────────────────────────────────────

  betButton.addEventListener('click', () => void placeBet());

  async function placeBet(): Promise<void> {
    const invalid = [...betFields].find(([, field]) => !field.valid());
    if (invalid) {
      failure.textContent = `${invalid[0]} is not filled in correctly.`;
      return;
    }

    const bet: EngineBet = { amount: levels[levelIndex]! };
    for (const [name, field] of betFields) {
      const value = field.value();
      if (value !== undefined) bet[name] = value;
    }

    await run(() => api.bet(gameCode, bet, crypto.randomUUID()));
  }

  async function act(type: string): Promise<void> {
    if (!openRoundId) return;
    const roundId = openRoundId;

    const action: EngineAction = { type };
    for (const [name, field] of extraFields) {
      const value = field.value();
      if (value !== undefined && field.valid()) action[name] = value;
    }

    await run(() => api.action(gameCode, roundId, action));
  }

  /**
   * One place for every call that plays a round, because they all do the same
   * four things: lock the controls, show the answer, unlock, and put the
   * failure on screen instead of in the console. A 400 here is the server's
   * validator disagreeing with the form, which is the case this UI cannot
   * rule out — it draws from a schema it does not fully understand.
   */
  async function run(call: () => Promise<RoundResponse>): Promise<void> {
    if (busy) return;
    busy = true;
    failure.textContent = '';
    setEnabled(false);

    try {
      const round = await call();
      if (!alive) return;
      show(round);
    } catch (error) {
      if (!alive) return;
      failure.textContent =
        error instanceof GameApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'The bet could not be placed.';
    } finally {
      busy = false;
      if (alive) setEnabled(true);
    }
  }

  function setEnabled(on: boolean): void {
    betButton.disabled = !on || openRoundId !== null;
    for (const node of actionBar.querySelectorAll('button')) node.disabled = !on;
  }

  function show(round: RoundResponse): void {
    onBalance(round.balance);

    if (round.settled) {
      openRoundId = null;
      roundPanel.element.hidden = true;
      showResult(round);
      betPanel.element.hidden = false;
      return;
    }

    openRoundId = round.roundId;
    resultPanel.element.hidden = true;
    betPanel.element.hidden = true;
    roundPanel.element.hidden = false;
    showView(round.state ?? {});
  }

  // ── The open round ───────────────────────────────────────────────────────

  const extraFields = new Map<string, Field>();

  function showView(view: Record<string, unknown>): void {
    viewBody.replaceChildren();
    for (const [key, value] of Object.entries(view)) {
      if (key === 'allowedActions') continue;
      viewBody.append(keyValue(key, value));
    }
    showActions(view);
  }

  /**
   * The moves on offer are the engine's `action.type` enum, narrowed by the
   * `allowedActions` the view publishes. An engine that does not publish them
   * gets every move it declared and rejects the ones that do not apply, which
   * is worse to play but still correct.
   */
  function showActions(view: Record<string, unknown>): void {
    const declared = actionTypesOf(actionSchema);
    const allowed = view.allowedActions;
    const offered = Array.isArray(allowed)
      ? declared.filter((type) => (allowed as unknown[]).includes(type))
      : declared;

    buildExtras(offered);

    actionBar.replaceChildren();
    for (const type of offered) {
      const move = button(titleOf(type), 'gen-action');
      move.addEventListener('click', () => void act(type));
      actionBar.append(move);
    }
  }

  /**
   * A move can carry more than its name — which tile to open, which lane to
   * climb. Those are ordinary schema fields, so they are drawn the same way a
   * bet field is, above the moves that use them.
   *
   * When there is exactly one move on offer the extra control acts on its
   * own: picking a tile in a game whose only move is "reveal" can mean
   * nothing else, and making the player press a second button for it is
   * ceremony. With two or more moves the value is chosen first and the move
   * second, because which one it belongs to is not ours to guess.
   */
  function buildExtras(offered: string[]): void {
    extraBody.replaceChildren();
    extraFields.clear();

    for (const [name, fieldSchema] of fieldsOf(actionSchema)) {
      if (name === 'type') continue;
      const field = renderField(name, fieldSchema, false, { grid: true });
      extraFields.set(name, field);
      if (offered.length === 1) {
        field.element.addEventListener('click', (event) => {
          if ((event.target as HTMLElement).tagName === 'BUTTON') void act(offered[0]!);
        });
      }
      extraBody.append(field.element);
    }
  }

  // ── The settled round ────────────────────────────────────────────────────

  function showResult(round: RoundResponse): void {
    const result = (round.result ?? {}) as Record<string, unknown>;
    resultPanel.body.replaceChildren();
    resultPanel.element.hidden = false;

    // A round can settle with no result of its own — a void, a cancellation —
    // and the balance is then the only thing that moved.
    const totalWin = typeof result.totalWin === 'string' ? result.totalWin : '0';
    const total = document.createElement('p');
    total.className = BigInt(totalWin) > 0n ? 'gen-total win' : 'gen-total';
    total.textContent = formatMoney(totalWin, currency);
    resultPanel.body.append(total);

    const headline = document.createElement('div');
    headline.className = 'gen-headline';
    for (const key of HEADLINE_KEYS) {
      if (result[key] !== undefined) headline.append(keyValue(key, result[key]));
    }
    if (headline.childElementCount > 0) resultPanel.body.append(headline);

    const rest = document.createElement('div');
    rest.className = 'gen-kv';
    const proof = document.createElement('div');
    proof.className = 'gen-kv';

    for (const [key, value] of Object.entries(result)) {
      if (key === 'totalWin' || HEADLINE_KEYS.includes(key)) continue;
      (PROOF_KEYS.includes(key) ? proof : rest).append(keyValue(key, value));
    }

    if (rest.childElementCount > 0) resultPanel.body.append(rest);
    if (proof.childElementCount > 0) {
      const details = document.createElement('details');
      details.className = 'gen-proof';
      const summary = document.createElement('summary');
      summary.textContent = 'Proof';
      details.append(summary, proof);
      resultPanel.body.append(details);
    }
  }

  // ── Resuming ─────────────────────────────────────────────────────────────

  /**
   * A multi-step round outlives the page it was opened on. The platform is
   * the one that knows whether this player left one open, so it is asked
   * rather than inferred from the session payload.
   */
  if (multiStep) {
    void api
      .active(gameCode)
      .then((round) => {
        if (alive && round) show(round);
      })
      .catch(() => {
        /* An unreachable resume is not worth a banner; the bet form works. */
      });
  }

  return {
    destroy(): void {
      alive = false;
      root.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Small DOM helpers
// ---------------------------------------------------------------------------

function panel(heading: string): { element: HTMLElement; body: HTMLElement } {
  const element = document.createElement('section');
  element.className = 'gen-panel';

  const title = document.createElement('h2');
  title.className = 'gen-panel-title';
  title.textContent = heading;

  const body = document.createElement('div');
  body.className = 'gen-panel-body';

  element.append(title, body);
  return { element, body };
}

function button(text: string, className: string): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = className;
  element.textContent = text;
  return element;
}

function label(text: string): HTMLSpanElement {
  const element = document.createElement('span');
  element.className = 'gen-field-label';
  element.textContent = text;
  return element;
}

/** One line of the view or the result. Arrays read better as chips. */
function keyValue(key: string, value: unknown): HTMLElement {
  const row = document.createElement('div');
  row.className = 'gen-kv-row';

  const name = document.createElement('span');
  name.className = 'gen-kv-key';
  name.textContent = titleOf(key);

  const shown = document.createElement('span');
  shown.className = 'gen-kv-value';

  if (Array.isArray(value)) {
    for (const item of value) {
      const chip = document.createElement('span');
      chip.className = 'gen-chip';
      chip.textContent = typeof item === 'object' ? JSON.stringify(item) : String(item);
      shown.append(chip);
    }
  } else if (value !== null && typeof value === 'object') {
    shown.textContent = JSON.stringify(value);
  } else {
    shown.textContent = String(value);
  }

  row.append(name, shown);
  return row;
}

function titleOf(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
